import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RemoveIcon from '@mui/icons-material/Remove';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';

const emptyForm = {
  numeroOficio: '',
  orgaoId: '',
  assunto: '',
  categoriaAssuntoId: '',
  descricao: '',
  dataRecebimento: new Date().toISOString().slice(0, 10),
  dataLimiteResposta: '',
  responsaveis: [],
  resumoIa: '',
};

export default function JuridicoOficioFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user, unidadeSelecionada } = useAuth();
  const [form, setForm] = useState(emptyForm);
  const [arquivo, setArquivo] = useState(null);
  const [orgaos, setOrgaos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [dialogOrgao, setDialogOrgao] = useState({ open: false, nome: '', saving: false });
  const [dialogCat, setDialogCat] = useState({ open: false, nome: '', saving: false });
  const [removendo, setRemovendo] = useState(null);

  const carregarAux = async () => {
    try {
      const [rOrg, rCat, rUsu] = await Promise.all([
        apiFetch('/api/juridico/orgaos-emissores'),
        apiFetch('/api/juridico/categorias-assunto'),
        apiFetch('/api/usuarios'),
      ]);
      const dOrg = await rOrg.json().catch(() => []);
      const dCat = await rCat.json().catch(() => []);
      const dUsu = await rUsu.json().catch(() => []);
      if (rOrg.ok) setOrgaos(Array.isArray(dOrg) ? dOrg : []);
      if (rCat.ok) setCategorias(Array.isArray(dCat) ? dCat : []);
      if (rUsu.ok) setUsuarios(Array.isArray(dUsu) ? dUsu : []);
    } catch (e) {
      console.warn(e);
    }
  };

  useEffect(() => {
    carregarAux();
  }, []);

  useEffect(() => {
    if (!isEdit) {
      setForm((p) => ({
        ...p,
        responsaveis: user?.id
          ? [{ id: user.id, nome: user.nome || user.email || String(user.id) }]
          : [],
      }));
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const res = await apiFetch(`/api/juridico/oficios/${id}`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Erro ao carregar');
        const resp =
          Array.isArray(data.responsaveis) && data.responsaveis.length
            ? data.responsaveis
            : data.responsavelInternoId
              ? [{ id: data.responsavelInternoId, nome: data.responsavelNome || String(data.responsavelInternoId) }]
              : [];
        setForm({
          numeroOficio: data.numeroOficio || '',
          orgaoId: data.orgaoId != null ? String(data.orgaoId) : '',
          assunto: data.assunto || '',
          categoriaAssuntoId: data.categoriaAssuntoId != null ? String(data.categoriaAssuntoId) : '',
          descricao: data.descricao || '',
          dataRecebimento: (data.dataRecebimento || '').slice(0, 10),
          dataLimiteResposta: (data.dataLimiteResposta || '').slice(0, 10),
          responsaveis: resp,
          resumoIa: data.resumoIa || '',
        });
      } catch (e) {
        setErro(e.message || 'Erro ao carregar ofício');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, user?.id, user?.nome, user?.email]);

  const criarOrgao = async () => {
    const nome = String(dialogOrgao.nome || '').trim();
    if (!nome) return;
    setDialogOrgao((p) => ({ ...p, saving: true }));
    try {
      const res = await apiFetch('/api/juridico/orgaos-emissores', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nome, tipo: 'outro' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao criar órgão');
      await carregarAux();
      setForm((p) => ({ ...p, orgaoId: String(data.id) }));
      setDialogOrgao({ open: false, nome: '', saving: false });
    } catch (e) {
      setErro(e.message || 'Falha ao criar órgão');
      setDialogOrgao((p) => ({ ...p, saving: false }));
    }
  };

  const criarCategoria = async () => {
    const nome = String(dialogCat.nome || '').trim();
    if (!nome) return;
    setDialogCat((p) => ({ ...p, saving: true }));
    try {
      const res = await apiFetch('/api/juridico/categorias-assunto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nome }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao criar categoria');
      await carregarAux();
      setForm((p) => ({ ...p, categoriaAssuntoId: String(data.id) }));
      setDialogCat({ open: false, nome: '', saving: false });
    } catch (e) {
      setErro(e.message || 'Falha ao criar categoria');
      setDialogCat((p) => ({ ...p, saving: false }));
    }
  };

  const removerOpcao = async (tipo) => {
    const targetId = tipo === 'orgao' ? form.orgaoId : form.categoriaAssuntoId;
    if (!targetId) {
      setErro(tipo === 'orgao' ? 'Selecione um órgão para remover.' : 'Selecione uma categoria para remover.');
      return;
    }
    if (!window.confirm(`Remover esta opção da lista?`)) return;
    setRemovendo(tipo);
    setErro('');
    try {
      const url =
        tipo === 'orgao'
          ? `/api/juridico/orgaos-emissores/${targetId}`
          : `/api/juridico/categorias-assunto/${targetId}`;
      const res = await apiFetch(url, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao remover');
      await carregarAux();
      if (tipo === 'orgao') setForm((p) => ({ ...p, orgaoId: '' }));
      else setForm((p) => ({ ...p, categoriaAssuntoId: '' }));
      setMsg('Opção removida da lista.');
    } catch (e) {
      setErro(e.message || 'Falha ao remover');
    } finally {
      setRemovendo(null);
    }
  };

  const salvar = async () => {
    setErro('');
    setMsg('');
    if (!form.assunto.trim()) {
      setErro('Assunto é obrigatório.');
      return;
    }
    if (!form.dataRecebimento) {
      setErro('Data de recebimento é obrigatória.');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('assunto', form.assunto.trim());
      fd.append('numeroOficio', form.numeroOficio || '');
      fd.append('descricao', form.descricao || '');
      fd.append('dataRecebimento', form.dataRecebimento);
      fd.append('dataLimiteResposta', form.dataLimiteResposta || '');
      if (form.orgaoId) fd.append('orgaoId', form.orgaoId);
      if (form.categoriaAssuntoId) fd.append('categoriaAssuntoId', form.categoriaAssuntoId);
      const ids = (form.responsaveis || []).map((u) => u.id).filter(Boolean);
      fd.append('responsavelInternoIds', JSON.stringify(ids));
      if (ids[0]) fd.append('responsavelInternoId', String(ids[0]));
      if (isEdit && form.resumoIa != null) fd.append('resumoIa', form.resumoIa);
      if (arquivo) fd.append('arquivo', arquivo);

      const res = await apiFetch(isEdit ? `/api/juridico/oficios/${id}` : '/api/juridico/oficios', {
        method: isEdit ? 'PUT' : 'POST',
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar');
      setMsg('Salvo com sucesso.');
      navigate(`/juridico/oficios/${data.id}`);
    } catch (e) {
      setErro(e.message || 'Falha ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 960, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/juridico/oficios')} sx={{ textTransform: 'none' }}>
          Voltar
        </Button>
        <Typography variant="h5" fontWeight={800}>
          {isEdit ? 'Editar ofício' : 'Novo ofício'}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Unidade: {unidadeSelecionada?.codigo || '—'}
        {!isEdit && ' · Após salvar, a análise por IA é disparada automaticamente.'}
      </Typography>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>
          {erro}
        </Alert>
      )}
      {msg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>
          {msg}
        </Alert>
      )}

      <Paper sx={{ p: 2.5, borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField
              label="Assunto *"
              fullWidth
              size="small"
              value={form.assunto}
              onChange={(e) => setForm((p) => ({ ...p, assunto: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Nº do ofício"
              fullWidth
              size="small"
              value={form.numeroOficio}
              onChange={(e) => setForm((p) => ({ ...p, numeroOficio: e.target.value }))}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <FormControl fullWidth size="small">
                <InputLabel>Órgão emissor</InputLabel>
                <Select
                  label="Órgão emissor"
                  value={form.orgaoId}
                  onChange={(e) => setForm((p) => ({ ...p, orgaoId: e.target.value }))}
                >
                  <MenuItem value="">—</MenuItem>
                  {orgaos.map((o) => (
                    <MenuItem key={o.id} value={String(o.id)}>
                      {o.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title="Adicionar órgão">
                <IconButton color="primary" onClick={() => setDialogOrgao({ open: true, nome: '', saving: false })}>
                  <AddIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remover órgão selecionado">
                <span>
                  <IconButton
                    color="error"
                    disabled={!form.orgaoId || removendo === 'orgao'}
                    onClick={() => removerOpcao('orgao')}
                  >
                    {removendo === 'orgao' ? <CircularProgress size={18} /> : <RemoveIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <FormControl fullWidth size="small">
                <InputLabel>Categoria</InputLabel>
                <Select
                  label="Categoria"
                  value={form.categoriaAssuntoId}
                  onChange={(e) => setForm((p) => ({ ...p, categoriaAssuntoId: e.target.value }))}
                >
                  <MenuItem value="">—</MenuItem>
                  {categorias.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                      {c.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title="Adicionar categoria">
                <IconButton color="primary" onClick={() => setDialogCat({ open: true, nome: '', saving: false })}>
                  <AddIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remover categoria selecionada">
                <span>
                  <IconButton
                    color="error"
                    disabled={!form.categoriaAssuntoId || removendo === 'categoria'}
                    onClick={() => removerOpcao('categoria')}
                  >
                    {removendo === 'categoria' ? <CircularProgress size={18} /> : <RemoveIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Data recebimento *"
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.dataRecebimento}
              onChange={(e) => setForm((p) => ({ ...p, dataRecebimento: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Data limite resposta"
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.dataLimiteResposta}
              onChange={(e) => setForm((p) => ({ ...p, dataLimiteResposta: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              multiple
              options={usuarios}
              value={form.responsaveis}
              onChange={(_, v) => setForm((p) => ({ ...p, responsaveis: v }))}
              getOptionLabel={(o) => o.nome || o.email || String(o.id)}
              isOptionEqualToValue={(a, b) => Number(a.id) === Number(b.id)}
              renderInput={(params) => (
                <TextField {...params} label="Responsáveis internos" size="small" placeholder="Selecionar…" />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Descrição"
              fullWidth
              size="small"
              multiline
              minRows={3}
              value={form.descricao}
              onChange={(e) => setForm((p) => ({ ...p, descricao: e.target.value }))}
            />
          </Grid>
          {isEdit && (
            <Grid item xs={12}>
              <TextField
                label="Resumo IA (editável)"
                fullWidth
                size="small"
                multiline
                minRows={4}
                value={form.resumoIa}
                onChange={(e) => setForm((p) => ({ ...p, resumoIa: e.target.value }))}
                helperText="Pode preencher manualmente se a IA falhar"
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Button variant="outlined" component="label" sx={{ textTransform: 'none' }}>
              {arquivo ? `Arquivo: ${arquivo.name}` : isEdit ? 'Substituir arquivo do ofício' : 'Anexar ofício (PDF/imagem)'}
              <input
                type="file"
                hidden
                accept=".pdf,application/pdf,image/*"
                onChange={(e) => setArquivo(e.target.files?.[0] || null)}
              />
            </Button>
          </Grid>
        </Grid>

        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 3 }}>
          <Button onClick={() => navigate('/juridico/oficios')} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            disabled={saving}
            onClick={salvar}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Salvar
          </Button>
        </Stack>
      </Paper>

      <Dialog open={dialogOrgao.open} onClose={() => !dialogOrgao.saving && setDialogOrgao({ open: false, nome: '', saving: false })}>
        <DialogTitle>Novo órgão emissor</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Nome"
            value={dialogOrgao.nome}
            onChange={(e) => setDialogOrgao((p) => ({ ...p, nome: e.target.value }))}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={dialogOrgao.saving} onClick={() => setDialogOrgao({ open: false, nome: '', saving: false })}>
            Cancelar
          </Button>
          <Button variant="contained" disabled={dialogOrgao.saving || !dialogOrgao.nome.trim()} onClick={criarOrgao}>
            {dialogOrgao.saving ? <CircularProgress size={18} /> : 'Adicionar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogCat.open} onClose={() => !dialogCat.saving && setDialogCat({ open: false, nome: '', saving: false })}>
        <DialogTitle>Nova categoria</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Nome"
            value={dialogCat.nome}
            onChange={(e) => setDialogCat((p) => ({ ...p, nome: e.target.value }))}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={dialogCat.saving} onClick={() => setDialogCat({ open: false, nome: '', saving: false })}>
            Cancelar
          </Button>
          <Button variant="contained" disabled={dialogCat.saving || !dialogCat.nome.trim()} onClick={criarCategoria}>
            {dialogCat.saving ? <CircularProgress size={18} /> : 'Adicionar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
